// T-358 -- Feeder Variant Modal JS.
// File destination (deploy): theme/live/assets/dpc-feeder-modal.js
//
// Architecture: a presentation layer over Prestige's native <variant-picker>.
// Mirror option buttons proxy to hidden native radios so Prestige drives price,
// ATC variant id, ?variant= URL, gallery image, and availability.
//
// Rerender survival: Prestige's Section Rendering API rebuilds the entire
// main-product section (~280-500ms) on every variant change, which destroys
// the <dialog> and all listeners attached to section nodes. Three mitigations:
//   1. liftDialogToBody()  -- moves <dialog> out of the section to <body>,
//      where Prestige cannot touch it. Ported from cats-atelier-pdp.js T-276.
//   2. Delegated listeners -- all event listeners attached to `document`, never
//      to nodes that get replaced. Safe across unlimited rerenders.
//   3. Double-fire restore -- syncFromNative() called at 450ms + 950ms after
//      every proxied click and after every native variant-picker change event,
//      to re-sync the mirror UI from the refreshed DOM.
//
// CRITICAL PRE-DEPLOY CHECK (native radio DOM structure):
// setNativeOption() targets:
//   variant-picker[context="main_product"] input[type="radio"][data-option-position]
//   with radio.value === option_value.id (numeric string from Shopify).
// This was verified by static analysis of option-value.liquid in the 06-08 session
// and PASSED the pre-deploy native-radio DOM check on the test theme (2026-06-14):
// feeder radios carry data-option-position + numeric option_value.id value.
// Re-confirm on the test theme after any wiring change:
//   1. Deploy to Test theme (wiring edits A + B applied, tag added to one feeder).
//   2. Open a feeder PDP in DevTools > Elements.
//   3. Confirm: variant-picker[context="main_product"] exists in the DOM.
//   4. Confirm: child input[type="radio"] elements are present, each carrying
//      data-option-position (1-indexed) and value = numeric option_value.id string.
//   5. Click a mirror option button in the modal. Confirm in the Network tab that
//      a ?sections=main-product fetch fires (proving Prestige ran its pipeline).
// If step 4 fails (radios have a different structure), setNativeOption() returns
// false and falls through to the local-only fallback path -- price/ATC/URL/gallery
// will NOT update. This is the highest-risk unverified claim in the build.
// This no-write overnight run cannot perform the check.
//
// Selectors for product-page.js T-358 assertions:
//   [data-dpc-vm]               wrapper gate
//   [data-dpc-vm-open]          trigger pill button
//   [data-dpc-vm-done]          Done button
//   [data-dpc-vm-group]         option group containers
//   [data-dpc-vm-option]        mirror option buttons
//   [data-dpc-vm-preview]       preview image
//   [data-dpc-vm-summary-price] summary price line
//   [data-dpc-vm-trigger-price] trigger pill price
//   dialog.dpc-vm-dialog        dialog element
//   .dpc-vm-option--active      selected-option state
//   .dpc-vm-option--unavailable OOS option state
//   .dpc-vm-trigger-category    category label
//
// References: design doc overnight-2026-06-07/t358-feeder-modal-design.md;
// CA pattern cats-atelier-pdp.js (liftDialogToBody L235-268).
// Memory: feedback_dialog_lift_to_body_pattern.

(function () {
  'use strict';

  // Early exit if this page has no feeder modal wrapper.
  if (!document.querySelector('[data-dpc-vm]')) return;

  // -- Variant data ---------------------------------------------------------------
  // Parsed once from the embedded JSON blob. Each entry:
  //   { id, title, available, price_cents, option1, option2, option3,
  //     featured_image_src, featured_image_alt }
  var variantData = [];
  var variantBlob = document.querySelector('[data-dpc-vm-variants]');
  if (variantBlob) {
    try { variantData = JSON.parse(variantBlob.textContent || '[]'); } catch (e) { /* ignore parse errors */ }
  }

  // Number of options on this product (1-3). Derived from the mirror groups.
  var optionCount = (function () {
    var groups = document.querySelectorAll('[data-dpc-vm-group]');
    var max = 0;
    groups.forEach(function (g) {
      var pos = parseInt(g.dataset.optionPosition, 10) || 0;
      if (pos > max) max = pos;
    });
    return max;
  }());

  // -- State ----------------------------------------------------------------------
  // currentValues[position] = option_value_id (string, matches native radio value).
  // Populated by readNativeSelection() on every sync.
  var currentValues = {};

  // -- Helpers: read native picker ------------------------------------------------

  /**
   * Read all currently-checked native radios and return a map of
   * { position: option_value_id } (string to string).
   * Works for block / swatch / variant_image selector styles -- all emit
   * <input type="radio" value="{option_value.id}" data-option-position="{n}">
   * via option-value.liquid (verified against live theme source).
   *
   * PRE-DEPLOY: confirm this selector structure by live DevTools inspection
   * on a feeder PDP on the test theme -- see file header for exact steps.
   */
  function readNativeSelection() {
    var result = {};
    var radios = document.querySelectorAll(
      'variant-picker[context="main_product"] input[type="radio"]:checked, ' +
      'variant-picker:not([context]) input[type="radio"]:checked'
    );
    radios.forEach(function (radio) {
      var pos = radio.dataset.optionPosition;
      if (pos) result[pos] = radio.value;
    });
    return result;
  }

  /**
   * Click the native radio matching a given option position + option_value_id.
   * Prestige listens for the change on <variant-picker> and runs its full
   * pipeline: price update, ATC variant id, ?variant= URL, gallery,
   * Section Rendering API rerender.
   * Returns true if the radio was found and clicked, false otherwise.
   *
   * PRE-DEPLOY: if this returns false unexpectedly, check DevTools to confirm
   * the radio value attribute contains the numeric option_value.id (not the
   * human-readable option name). See file header for the exact verification steps.
   */
  function setNativeOption(optionPosition, optionValueId) {
    var pos = String(optionPosition);
    var id  = String(optionValueId);
    var radios = document.querySelectorAll(
      'variant-picker[context="main_product"] input[type="radio"][data-option-position], ' +
      'variant-picker:not([context]) input[type="radio"][data-option-position]'
    );
    for (var i = 0; i < radios.length; i++) {
      var r = radios[i];
      if (r.dataset.optionPosition === pos && r.value === id && !r.checked) {
        r.click();
        return true;
      }
    }
    return false;
  }

  // -- Helpers: variant resolution ------------------------------------------------

  /**
   * Given the current option-value-id selections (currentValues map), find
   * the best-matching variant from variantData.
   * "Best" = most positions matched; fully-matched preferred over partial.
   * Uses human-text matching via the mirror button data-option-value attributes
   * because variantData stores option1/2/3 as human text (Shopify variant object).
   */
  function resolveVariant(values) {
    if (!variantData.length) return null;
    var best = null, bestScore = -1;
    variantData.forEach(function (v) {
      var score = 0;
      for (var pos = 1; pos <= optionCount; pos++) {
        var selId = values[pos];
        if (!selId) continue;
        // Translate option_value_id to human text via the mirror button's data attribute.
        var mirrorBtn = document.querySelector(
          '[data-dpc-vm-option][data-option-position="' + pos + '"][data-option-value-id="' + CSS.escape(selId) + '"]'
        );
        var humanVal = mirrorBtn ? mirrorBtn.dataset.optionValue : null;
        var vVal = (pos === 1 ? v.option1 : pos === 2 ? v.option2 : v.option3);
        if (humanVal && vVal && humanVal.toLowerCase() === vVal.toLowerCase()) score++;
      }
      if (score > bestScore) { best = v; bestScore = score; }
    });
    return best;
  }

  // -- Helpers: availability ------------------------------------------------------

  /**
   * For each mirror option button, determine whether there exists at least one
   * available variant consistent with:
   *   - that button's value at its position, AND
   *   - the currently-selected values at all OTHER positions.
   * If none exists, mark the button .dpc-vm-option--unavailable + disabled + aria-disabled.
   * Called after every syncFromNative() to keep the greying accurate.
   */
  function refreshAvailability() {
    for (var pos = 1; pos <= optionCount; pos++) {
      var buttons = document.querySelectorAll(
        '[data-dpc-vm-option][data-option-position="' + pos + '"]'
      );
      buttons.forEach(function (btn) {
        var testValues = Object.assign({}, currentValues);
        testValues[String(pos)] = btn.dataset.optionValueId;

        // Translate testValues (id map) to human-text map for variant matching.
        var humanMap = {};
        for (var p = 1; p <= optionCount; p++) {
          var idVal = testValues[String(p)];
          if (!idVal) continue;
          var mb = document.querySelector(
            '[data-dpc-vm-option][data-option-position="' + p + '"][data-option-value-id="' + CSS.escape(idVal) + '"]'
          );
          humanMap[p] = mb ? mb.dataset.optionValue : null;
        }

        var hasAvailable = variantData.some(function (v) {
          for (var p = 1; p <= optionCount; p++) {
            if (!humanMap[p]) continue;
            var vVal = (p === 1 ? v.option1 : p === 2 ? v.option2 : v.option3);
            if (vVal && vVal.toLowerCase() !== humanMap[p].toLowerCase()) return false;
          }
          return v.available;
        });

        if (hasAvailable) {
          btn.classList.remove('dpc-vm-option--unavailable');
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
        } else {
          btn.classList.add('dpc-vm-option--unavailable');
          btn.setAttribute('disabled', 'true');
          btn.setAttribute('aria-disabled', 'true');
        }
      });
    }
  }

  // -- Helpers: UI sync -----------------------------------------------------------

  /**
   * Idempotent "truth reconciliation": reads the currently-checked native radios
   * and updates all mirror UI: active state, trigger label, trigger price,
   * summary line, preview image, OOS indicator, Done disabled state.
   * Called after every event and after every rerender settle.
   */
  function syncFromNative() {
    currentValues = readNativeSelection();

    // Mirror button active state.
    document.querySelectorAll('[data-dpc-vm-option]').forEach(function (btn) {
      var pos    = btn.dataset.optionPosition;
      var id     = btn.dataset.optionValueId;
      var active = currentValues[pos] && currentValues[pos] === id;
      btn.classList.toggle('dpc-vm-option--active', !!active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    // Resolve the best-matching variant from the JSON blob.
    var v = resolveVariant(currentValues);
    if (!v) return;

    // Trigger label.
    var labelEl = document.querySelector('.dpc-vm-trigger-label');
    if (labelEl) labelEl.textContent = v.title || '';

    // Trigger price.
    var triggerPrice = document.querySelector('[data-dpc-vm-trigger-price]');
    if (triggerPrice) triggerPrice.textContent = formatMoney(v.price_cents);

    // Dialog summary line.
    var summaryPrice = document.querySelector('[data-dpc-vm-summary-price]');
    if (summaryPrice) summaryPrice.textContent = formatMoney(v.price_cents);
    var summaryTitle = document.querySelector('[data-dpc-vm-summary-title]');
    if (summaryTitle) summaryTitle.textContent = v.title || '';

    // Preview image (only update if src actually changes -- avoids flicker).
    var preview = document.querySelector('[data-dpc-vm-preview]');
    if (preview && v.featured_image_src) {
      if (preview.getAttribute('src') !== v.featured_image_src) {
        preview.setAttribute('src', v.featured_image_src);
        preview.setAttribute('alt', v.featured_image_alt || v.title || '');
      }
    }

    // OOS indicator.
    var oosEl = document.querySelector('[data-dpc-vm-oos]');
    if (oosEl) oosEl.hidden = (v.available !== false);

    // Done button: disabled when the selected variant is OOS.
    var doneBtn = document.querySelector('[data-dpc-vm-done]');
    if (doneBtn) {
      doneBtn.disabled = (v.available === false);
      doneBtn.setAttribute('aria-disabled', v.available === false ? 'true' : 'false');
    }

    // T-358 (2026-06-14): Add to Basket button disabled when the selected variant is OOS.
    var addBtn = document.querySelector('[data-dpc-vm-add]');
    if (addBtn) {
      addBtn.disabled = (v.available === false);
      addBtn.setAttribute('aria-disabled', v.available === false ? 'true' : 'false');
    }

    // Availability greying on all option buttons given the new selection.
    refreshAvailability();
  }

  // -- Optimistic update ----------------------------------------------------------
  // T-358 (2026-06-14): instantly reflect an option click in the dialog (preview image,
  // price, title, active state) without waiting for the 450ms post-rerender syncFromNative().
  // Preloaded images make the preview swap immediate, so the dialog no longer lags the
  // main gallery. The later syncFromNative() reconciles against the authoritative native state.
  function applyOptimistic(pos, id) {
    document.querySelectorAll('[data-dpc-vm-option][data-option-position="' + pos + '"]').forEach(function (b) {
      var on = b.dataset.optionValueId === id;
      b.classList.toggle('dpc-vm-option--active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    var test = Object.assign({}, currentValues); test[pos] = id;
    var v = resolveVariant(test);
    if (!v) return;
    var preview = document.querySelector('[data-dpc-vm-preview]');
    if (preview && v.featured_image_src && preview.getAttribute('src') !== v.featured_image_src) {
      preview.setAttribute('src', v.featured_image_src);
      preview.setAttribute('alt', v.featured_image_alt || v.title || '');
    }
    var labelEl = document.querySelector('.dpc-vm-trigger-label'); if (labelEl) labelEl.textContent = v.title || '';
    var tp = document.querySelector('[data-dpc-vm-trigger-price]'); if (tp) tp.textContent = formatMoney(v.price_cents);
    var sp = document.querySelector('[data-dpc-vm-summary-price]'); if (sp) sp.textContent = formatMoney(v.price_cents);
    var st = document.querySelector('[data-dpc-vm-summary-title]'); if (st) st.textContent = v.title || '';
  }

  // -- Money formatting -----------------------------------------------------------
  // Formats Shopify price (integer pence) as GBP string. Strips ".00" for
  // whole pounds to match Shopify's money_without_trailing_zeros filter.
  function formatMoney(priceCents) {
    if (priceCents == null) return '';
    var pounds = (priceCents / 100).toFixed(2);
    if (pounds.endsWith('.00')) pounds = pounds.slice(0, -3);
    return '£' + pounds; // GBP sign (Unicode to avoid encoding issues in Shopify CDN)
  }

  // -- liftDialogToBody -----------------------------------------------------------
  // Ported verbatim from cats-atelier-pdp.js (T-276 Path A v2, 2026-05-12).
  // Moves dialog.dpc-vm-dialog to <body> so Prestige's Section Rendering API
  // cannot destroy it on variant change. Idempotent. Removes any duplicate
  // dialogs Prestige may re-create inside the section after a rerender.
  function liftDialogToBody() {
    var dialogs = document.querySelectorAll('dialog.dpc-vm-dialog');
    if (!dialogs.length) return;

    // Prefer an already-lifted dialog (parentNode === body) as canonical.
    var canonical = null;
    for (var i = 0; i < dialogs.length; i++) {
      if (dialogs[i].parentNode === document.body) { canonical = dialogs[i]; break; }
    }
    if (!canonical) canonical = dialogs[0];

    // Move to body if not already there.
    if (canonical.parentNode !== document.body) {
      document.body.appendChild(canonical);
    }

    // Remove any duplicates Prestige re-created inside the section.
    for (var j = 0; j < dialogs.length; j++) {
      if (dialogs[j] !== canonical && dialogs[j].parentNode !== document.body) {
        // Transfer open state to canonical before removing the duplicate.
        if (dialogs[j].open && !canonical.open && typeof canonical.showModal === 'function') {
          try { canonical.showModal(); } catch (e) { /* ignore */ }
        }
        dialogs[j].remove();
      }
    }
  }

  // -- Dialog open / close --------------------------------------------------------

  function openDialog() {
    var dlg = document.querySelector('dialog.dpc-vm-dialog');
    if (!dlg || dlg.open) return;
    if (typeof dlg.showModal === 'function') {
      try { dlg.showModal(); } catch (e) { /* ignore */ }
    }
    updateAriaExpanded(true);
  }

  function closeDialog() {
    var dlg = document.querySelector('dialog.dpc-vm-dialog');
    if (!dlg || !dlg.open) return;
    dlg.close();
    updateAriaExpanded(false);
  }

  function updateAriaExpanded(isOpen) {
    var trigger = document.querySelector('[data-dpc-vm-open]');
    if (trigger) trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  // -- Delegated event listeners --------------------------------------------------
  // All attached to `document` so they survive Prestige's section node-swaps.

  // Open trigger pill.
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-dpc-vm-open]')) {
      openDialog();
      // Sync in case a URL-param variant was pre-selected.
      syncFromNative();
    }
  });

  // Close button + Done button + backdrop click.
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-dpc-vm-close]') || e.target.closest('[data-dpc-vm-done]')) {
      closeDialog();
      return;
    }
    // Backdrop click: native <dialog> sets e.target to the dialog element itself.
    if (e.target.classList && e.target.classList.contains('dpc-vm-dialog')) {
      if (e.target.open) e.target.close();
      updateAriaExpanded(false);
    }
  });

  // T-358 (2026-06-14): Add to Basket — proxy the page's real Add-to-Cart button so
  // Prestige's add-to-cart + cart-drawer-open pipeline runs with the currently-selected
  // variant (already set on the native picker by setNativeOption). Close the dialog first
  // so the cart drawer is not obscured by the modal.
  document.addEventListener('click', function (e) {
    var add = e.target.closest('[data-dpc-vm-add]');
    if (!add) return;
    if (add.disabled || add.getAttribute('aria-disabled') === 'true') return;
    // Prestige's ATC button (buy-buttons.liquid -> button.liquid) is type="submit" with
    // NO name attribute, so button[name="add"] never matches. Use type="submit" inside
    // <buy-buttons> (the proven selector — see dpc-io-validation-tracking.js L116-121),
    // excluding any quick-buy/sticky duplicate.
    var atc = document.querySelector(
      'buy-buttons button[type="submit"], ' +
      'product-form button[type="submit"]:not(quick-buy button), ' +
      '.product-info__block-item[data-block-type="buy-buttons"] button[type="submit"], ' +
      'form[action*="/cart/add"] button[type="submit"]:not([form*="quick"])'
    );
    closeDialog();
    if (atc) { atc.click(); }
  });

  // ESC key: native <dialog> handles the close, we update aria-expanded after.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      var dlg = document.querySelector('dialog.dpc-vm-dialog[open]');
      if (dlg) {
        setTimeout(function () { updateAriaExpanded(false); }, 0);
      }
    }
  });

  // Mirror option button click: proxy to the matching native radio.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-dpc-vm-option]');
    if (!btn) return;
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    var pos = btn.dataset.optionPosition;
    var id  = btn.dataset.optionValueId;
    if (!pos || !id) return;

    // T-358 (2026-06-14): instant optimistic UI before the proxied native change settles.
    applyOptimistic(pos, id);

    var didProxy = setNativeOption(pos, id);

    // Double-fire restore: re-lift and re-sync after the rerender settles.
    // 450ms + 950ms matches the proven timing from cats-atelier-pdp.js.
    setTimeout(function () { liftDialogToBody(); syncFromNative(); }, 450);
    setTimeout(function () { liftDialogToBody(); syncFromNative(); }, 950);

    if (!didProxy) {
      // Fallback: native radio not found (unexpected DOM structure).
      // Apply active state locally so the UI is not stuck.
      // NOTE: this fallback does NOT trigger Prestige's pipeline -- price, ATC,
      // URL, and gallery will not update. If this path fires in practice, the
      // native radio DOM structure must be re-verified in DevTools (see file header).
      currentValues[pos] = id;
      syncFromNative();
    }
  });

  // Native variant-picker change (URL ?variant= deep-link or Prestige-internal
  // change): re-sync the mirror UI after the rerender settles.
  document.addEventListener('change', function (e) {
    if (!e.target.closest('variant-picker')) return;
    setTimeout(function () { liftDialogToBody(); syncFromNative(); }, 450);
    setTimeout(function () { liftDialogToBody(); syncFromNative(); }, 950);
  }, true);

  // -- Initialisation -------------------------------------------------------------

  // T-358 (2026-06-14): preload every variant's featured image so the in-dialog
  // preview swaps instantly on option change (no fetch-on-click lag — the image is
  // already in the browser cache by the time syncFromNative()/optimistic update sets src).
  function preloadVariantImages() {
    var seen = {};
    variantData.forEach(function (v) {
      var src = v.featured_image_src;
      if (src && !seen[src]) { seen[src] = true; var img = new Image(); img.src = src; }
    });
  }

  function init() {
    // Wait for the native variant-picker to be in the DOM.
    var picker = document.querySelector(
      'variant-picker[context="main_product"], variant-picker:not([context])'
    );
    if (!picker) {
      requestAnimationFrame(init);
      return;
    }

    // Initial sync: populate currentValues from the pre-selected native state.
    syncFromNative();

    // Preload variant images for instant preview swaps.
    preloadVariantImages();

    // Lift dialog to body so the very first rerender cannot destroy it.
    setTimeout(liftDialogToBody, 0);
  }

  init();

}());
